import React, { useEffect, useState } from "react";
import { Plus, Edit, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAppSelector } from "@/store/hooks";

interface BlogItem {
    _id: string;
    title: string;
    category: { _id: string; name: string };
    description?: string;
    status: boolean;
}

const Blogs: React.FC = () => {
    const [blogs, setBlogs] = useState<BlogItem[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const navigate = useNavigate();
    const { token } = useAppSelector((state) => state.auth);

    const baseUrl = import.meta.env.VITE_API_BASE_URL;

    // Fetch blogs
    useEffect(() => {
        axios
            .get(`${baseUrl}/blogs`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => {
                if (res.data.status && Array.isArray(res.data.data)) {
                    setBlogs(res.data.data);
                }
            })
            .catch((err) => {
                console.error("Failed to fetch blogs:", err.response?.data || err.message);
            });
    }, [baseUrl, token]);


    console.log("Blogs data:", blogs);
    const filteredData = blogs.filter(
        (blog) =>
            blog.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            blog.category?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const totalPages = Math.ceil(filteredData.length / entriesPerPage);
    const startIndex = (currentPage - 1) * entriesPerPage;
    const endIndex = startIndex + entriesPerPage;
    const currentData = filteredData.slice(startIndex, endIndex);

    console.log("Current data:", currentData);

    // Delete blog
    const handleDelete = async (id: string) => {
        try {
            await axios.delete(`${baseUrl}/blogs/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setBlogs((prev) => prev.filter((b) => b._id !== id));
            alert("Blog deleted successfully ✅");
        } catch (error: any) {
            console.error("Error deleting blog:", error.response?.data || error.message);
            alert(error.response?.data?.message || "Failed to delete blog");
        }
    };

    const handleEdit = (id: string) => {
        navigate(`/blogs/edit/${id}`);
    };

    const renderStatus = (status: boolean) => {
        return status ? (
            <span className="badge bg-success text-white fs-14 rounded-2">Active</span>
        ) : (
            <span className="badge bg-secondary text-white fs-14 rounded-2">Inactive</span>
        );
    };

    return (
        <div className="main-container container-fluid">
            {/* PAGE HEADER */}
            <div className="page-header">
                <div>
                    <ol className="breadcrumb">
                        <li className="breadcrumb-item">
                            <a href="/dashboard">Home</a>
                        </li>
                        <li className="breadcrumb-item active" aria-current="page">
                            Blogs
                        </li>
                    </ol>
                </div>
                <button
                    className="btn btn-primary d-flex align-items-center gap-2"
                    onClick={() => navigate("/blogs/add")}
                >
                    <Plus size={16} />
                    Add Blog
                </button>
            </div>

            <div className="row">
                <div className="col-12">
                    <div className="card table-card">
                        <div className="card-body">
                            <div className="table-responsive">
                                <div className="dataTables_wrapper dt-bootstrap5 no-footer">
                                    {/* Controls Row */}
                                    <div className="row mb-3">
                                        <div className="col-sm-12 col-md-6">
                                            <label>
                                                Show{" "}
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={entriesPerPage}
                                                    onChange={(e) => setEntriesPerPage(Number(e.target.value))}
                                                >
                                                    <option value={10}>10</option>
                                                    <option value={25}>25</option>
                                                    <option value={50}>50</option>
                                                    <option value={100}>100</option>
                                                </select>{" "}
                                                entries
                                            </label>
                                        </div>
                                        <div className="col-sm-12 col-md-6 text-end">
                                            <label>
                                                Search:
                                                <input
                                                    type="search"
                                                    className="form-control form-control-sm"
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                />
                                            </label>
                                        </div>
                                    </div>

                                    {/* Table */}
                                    <table className="table dataTable no-footer">
                                        <thead>
                                            <tr>
                                                <th>S.No</th>
                                                <th>Category</th>
                                                <th>Description</th>
                                                <th>Status</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentData.map((blog, index) => (
                                                <tr key={blog._id} className={index % 2 === 0 ? "odd" : "even"}>
                                                    <td>{startIndex + index + 1}</td>
                                                    <td>
                                                        {blog.categories && blog.categories.length > 0
                                                            ? blog.categories.map((cat: any) => cat.name).join(", ")
                                                            : "-"}
                                                    </td>
                                                    <td>{blog.description}</td>
                                                    <td>{renderStatus(blog.status)}</td>
                                                    <td>
                                                        <button
                                                            onClick={() => handleEdit(blog._id)}
                                                            className="btn bg-primary-transparent text-primary p-2 rounded-2 me-2"
                                                        >
                                                            <Edit size={16} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(blog._id)}
                                                            className="btn bg-danger-transparent text-danger p-2 rounded-2"
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                    {/* Pagination */}
                                    <div className="row mt-3">
                                        <div className="col-sm-12 col-md-5">
                                            <div className="dataTables_info">
                                                Showing {startIndex + 1} to {Math.min(endIndex, filteredData.length)} of{" "}
                                                {filteredData.length} entries
                                            </div>
                                        </div>
                                        <div className="col-sm-12 col-md-7">
                                            <ul className="pagination justify-content-end">
                                                <li
                                                    className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                                                >
                                                    <button
                                                        className="page-link"
                                                        onClick={() => setCurrentPage(currentPage - 1)}
                                                        disabled={currentPage === 1}
                                                    >
                                                        Previous
                                                    </button>
                                                </li>
                                                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                                    <li
                                                        key={page}
                                                        className={`page-item ${page === currentPage ? "active" : ""}`}
                                                    >
                                                        <button
                                                            className="page-link"
                                                            onClick={() => setCurrentPage(page)}
                                                        >
                                                            {page}
                                                        </button>
                                                    </li>
                                                ))}
                                                <li
                                                    className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                                                >
                                                    <button
                                                        className="page-link"
                                                        onClick={() => setCurrentPage(currentPage + 1)}
                                                        disabled={currentPage === totalPages}
                                                    >
                                                        Next
                                                    </button>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                    {/* End Pagination */}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Blogs;
